import { useEffect, useState, useSyncExternalStore } from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";

import {
  getAdminAuthSessionSnapshot,
  subscribeAdminAuthSession,
} from "@/lib/auth/admin-session";
import {
  getAuthSessionSnapshot,
  subscribeAuthSession,
} from "@/lib/auth/session";

/** Identity used before login, matching the anonymous LaunchDarkly context. */
const ANONYMOUS_IDENTITY = "anonymous";

/** Stop blocking the UI when LaunchDarkly never answers (offline, blocked SDK). */
export const FLAG_EVALUATION_TIMEOUT_MS = 3000;

function subscribeLocationHash(onChange: () => void): () => void {
  window.addEventListener("hashchange", onChange);
  return () => window.removeEventListener("hashchange", onChange);
}

function getLocationHash(): string {
  return window.location.hash;
}

/** Admin screens live under `#/admin`. The portal and the admin keep separate sessions. */
function isAdminSurface(hash: string): boolean {
  const path = (hash.startsWith("#") ? hash.slice(1) : hash).split("?")[0];
  return path === "/admin" || path.startsWith("/admin/");
}

/**
 * Email sent to LaunchDarkly for every flag on the surface on screen.
 * Admin routes use the admin session. Every other route uses the portal session.
 * Both sessions can be present together. Each screen evaluates as the person
 * who is using it, so a future flag does not need its own identity check.
 * A portal login does not stand in for an admin, and an admin login does not
 * change flags on the developer portal.
 */
function launchDarklyEmail(
  hash: string,
  portalEmail: string | undefined,
  adminEmail: string | undefined,
): string | undefined {
  const email = isAdminSurface(hash) ? adminEmail : portalEmail;
  const trimmed = email?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Points LaunchDarkly at the signed-in user and reports whether flags have been
 * evaluated for that identity.
 *
 * The client boots anonymous and `useFlags` returns the fail-closed defaults
 * until it initializes and identifies. A remembered session renders `#/home`
 * on that first frame, so without this signal the flag reads as off and the
 * Developer Portal redirects the user back to the docs (AES-290).
 */
export function useLaunchDarklyFlagsResolved(): boolean {
  const client = useLDClient();
  const portalSession = useSyncExternalStore(
    subscribeAuthSession,
    getAuthSessionSnapshot,
    getAuthSessionSnapshot,
  );
  const adminSession = useSyncExternalStore(
    subscribeAdminAuthSession,
    getAdminAuthSessionSnapshot,
    getAdminAuthSessionSnapshot,
  );
  const hash = useSyncExternalStore(
    subscribeLocationHash,
    getLocationHash,
    getLocationHash,
  );
  const email = launchDarklyEmail(
    hash,
    portalSession?.user.email,
    adminSession?.user.email,
  );
  const identity = email ?? ANONYMOUS_IDENTITY;
  const [evaluatedIdentity, setEvaluatedIdentity] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!client) {
      return;
    }

    let active = true;
    void client
      .identify(email ? { key: email, email } : { anonymous: true })
      .catch(() => undefined)
      .finally(() => {
        if (active) {
          setEvaluatedIdentity(identity);
        }
      });

    return () => {
      active = false;
    };
  }, [client, email, identity]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setTimedOut(true),
      FLAG_EVALUATION_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);

  return timedOut || (Boolean(client) && evaluatedIdentity === identity);
}
